import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Wallet, CheckCircle2, Clock, XCircle, Upload, CreditCard, Landmark, Gift, ExternalLink, HeartHandshake, CalendarDays, Award } from "lucide-react";
import { toast } from "sonner";
import { financeApi, apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const MONTH_NAMES = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const fmtMes = (k) => { const [y, m] = k.split("-"); return `${MONTH_NAMES[+m]} ${y}`; };
const waLink = (n) => `https://wa.me/${(n || "").replace(/\D/g, "")}`;

export default function Financiamiento() {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [info, setInfo] = useState(null);
  const [aids, setAids] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [pays, setPays] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [aidCtx, setAidCtx] = useState(null);
  const [conceptoCtx, setConceptoCtx] = useState(null);
  const [hijoId, setHijoId] = useState(null);

  const load = (hid) => {
    const h = hid !== undefined ? hid : hijoId;
    financeApi.statement(h).then((s) => {
      setSt(s);
      if (s.es_padre && !h && s.hijo_id) setHijoId(s.hijo_id);
    }).catch((e) => toast.error(apiError(e)));
    financeApi.payInfo().then(setInfo).catch(() => {});
    financeApi.aids(h).then(setAids).catch(() => {});
    financeApi.concepts(h).then(setConcepts).catch(() => {});
    financeApi.myPayments(h).then(setPays).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectHijo = (id) => { setHijoId(id); load(id); };

  const openPay = (aid = null, concepto = null) => { setAidCtx(aid); setConceptoCtx(concepto); setPayOpen(true); };

  if (!st || !info) return <div className="min-h-screen bg-cream flex items-center justify-center text-[#6B7688]">Cargando...</div>;

  const wa = info.whatsapp_staff;

  return (
    <div className="min-h-screen bg-cream" data-testid="financiamiento-page">
      <header className="bg-navy">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <button data-testid="fin-back-btn" onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Mi panel
          </button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl bg-[var(--gold)] flex items-center justify-center"><Wallet className="w-7 h-7 text-[#0A1C30]" /></span>
          <div>
            <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">FINANCIAMIENTO EDUCATIVO</p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0A1C30]">{st.es_padre ? "Estado de cuenta de tu hijo/a" : "Mi estado de cuenta"}</h1>
          </div>
        </div>

        {/* Selector de hijo/a (solo padres/madres con más de un hijo elegible) */}
        {st.es_padre && (st.hijos || []).filter((h) => h.elegible).length > 0 && (
          <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5" data-testid="fin-hijo-selector">
            <p className="text-xs font-extrabold tracking-[0.2em] text-[#B99B2E] mb-3">SELECCIONA A TU HIJO/A</p>
            <div className="flex flex-wrap gap-2">
              {(st.hijos || []).filter((h) => h.elegible).map((h) => (
                <button
                  key={h.id}
                  data-testid={`fin-hijo-${h.id}`}
                  onClick={() => selectHijo(h.id)}
                  className={`inline-flex flex-col items-start rounded-xl px-4 py-2.5 text-left transition-colors ${hijoId === h.id ? "bg-navy text-white" : "bg-[#F4F6F9] text-[#0A1C30] hover:bg-[#E9EDF2]"}`}
                >
                  <span className="text-sm font-extrabold">{h.nombre || "Hijo/a"}</span>
                  <span className={`text-xs ${hijoId === h.id ? "text-white/70" : "text-[#6B7688]"}`}>{h.programa}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Datos del usuario */}
        <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5" data-testid="fin-user-info">
          <p className="text-xs font-extrabold tracking-[0.2em] text-[#B99B2E] mb-3">MIS DATOS</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[["Nombre", st.nombre || "—"], ["Referencia Supera", st.referencia || "—"], ["País", st.pais || "—"], ["Programa", st.programa || "—"]].map(([l, v]) => (
              <div key={l}>
                <p className="text-xs font-bold text-[#6B7688]">{l}</p>
                <p className="text-sm font-extrabold text-[#0A1C30] mt-0.5 break-words">{v}</p>
              </div>
            ))}
          </div>
        </div>

        {st.suspendido && (
          <div className="rounded-2xl bg-[#FDECEC] ring-1 ring-[#E23B3B]/30 p-5" data-testid="fin-suspended-notice">
            <p className="font-extrabold text-[#C0392B]">Acceso suspendido por pago pendiente</p>
            <p className="text-sm text-[#8A4B45] mt-1">{st.suspension_reason || "Regulariza tu pago para reactivar tu acceso."}</p>
            <a href={waLink(wa)} target="_blank" rel="noreferrer" data-testid="fin-wa-suspend" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#22A860] text-white font-extrabold px-5 py-2 text-sm">
              Contactar al Staff — AQUÍ <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {!st.configurado ? (
          <div className="rounded-2xl bg-white ring-1 ring-black/5 p-8 text-center">
            <p className="font-bold text-[#0A1C30]">Tu plan de pagos aún no ha sido activado.</p>
            <p className="text-sm text-[#6B7688] mt-1">El equipo de Supera configurará tu fecha de inicio. Podrás ver aquí tu radiografía de pagos.</p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid sm:grid-cols-4 gap-4">
              {[["Programa", st.programa], ["Mensualidad", `${st.precio_mensual} ${st.moneda}`], ["Pagados", `${st.pagados}/${st.meses_totales}`], ["Faltan", `${st.faltan}`]].map(([l, v]) => (
                <div key={l} className="rounded-2xl bg-white ring-1 ring-black/5 p-4">
                  <p className="text-xs font-bold text-[#6B7688]">{l}</p>
                  <p className="text-lg font-extrabold text-[#0A1C30] mt-0.5">{v}</p>
                </div>
              ))}
            </div>
            {st.beca_pct > 0 && <p className="text-sm font-bold text-[#22A860]">🎓 Beca aplicada: {st.beca_pct}% de descuento sobre la mensualidad.</p>}
            <p className="text-sm text-[#6B7688]">Inicio: <b>{fmtMes(st.fecha_inicio)}</b> · Fin estimado: <b>{fmtMes(st.fin_esperado)}</b></p>

            {/* Months radiography */}
            <div>
              <h2 className="font-extrabold text-[#0A1C30] mb-3 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#E9A400]" /> Radiografía de pagos</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="fin-months">
                {st.meses.map((m) => (
                  <div key={m.mes} className={`rounded-2xl p-4 ring-1 ${m.pagado ? "bg-[#EAF7EF] ring-[#22A860]/30" : "bg-white ring-black/5"}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-extrabold text-[#0A1C30]">{fmtMes(m.mes)}</p>
                      {m.pagado ? <CheckCircle2 className="w-5 h-5 text-[#22A860]" /> : <Clock className="w-5 h-5 text-[#B0B6C0]" />}
                    </div>
                    <p className="text-xs mt-1 text-[#6B7688]">{m.pagado ? `Pagado ${m.fecha_pago || ""}` : `${m.monto} ${m.moneda} pendiente`}</p>
                  </div>
                ))}
              </div>
            </div>

            <button data-testid="fin-pay-btn" onClick={() => openPay(null)} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-8 py-3.5 transition-colors">
              <CreditCard className="w-5 h-5" /> Realizar un pago
            </button>
          </>
        )}

        {/* Otros conceptos de pago */}
        {concepts.length > 0 && (
          <div>
            <h2 className="font-extrabold text-[#0A1C30] mb-3 flex items-center gap-2"><Award className="w-5 h-5 text-[#E9A400]" /> Otros conceptos de pago</h2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="fin-concepts">
              {concepts.map((c) => (
                <div key={c.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-5 flex flex-col">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#E9A400]" />
                    <p className="font-extrabold text-[#0A1C30]">{c.titulo}</p>
                  </div>
                  {c.descripcion && <p className="text-sm text-[#6B7688] mt-1">{c.descripcion}</p>}
                  {c.fecha_fin && <p className="text-xs font-bold text-[#C0392B] mt-1">Disponible hasta el {c.fecha_fin}</p>}
                  <p className="text-lg font-extrabold text-[#0A1C30] mt-2">{Number(c.monto).toLocaleString()} {c.moneda}</p>
                  <button data-testid={`fin-concept-pay-${c.id}`} onClick={() => openPay(null, c)} className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-navy text-white font-bold px-4 py-2 text-sm hover:bg-[#0d2440]">
                    <CreditCard className="w-4 h-4" /> Pagar este concepto
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mis pagos */}
        {pays.length > 0 && (
          <div>
            <h2 className="font-extrabold text-[#0A1C30] mb-3">Mis pagos enviados</h2>
            <div className="space-y-2" data-testid="fin-payments">
              {pays.map((p) => (
                <div key={p.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-bold text-[#0A1C30]">{p.concepto ? p.concepto : `${p.programa} · ${fmtMes(p.mes_cubierto)}`}</p>
                    <p className="text-xs text-[#6B7688]">{p.monto} {p.moneda} · {p.metodo} · pagado {p.fecha_pago}</p>
                    {p.status === "rechazado" && (
                      <p className="text-xs text-[#C0392B] mt-1">{p.rejection_reason} — <a href={waLink(wa)} target="_blank" rel="noreferrer" className="font-extrabold underline">Contactar Staff AQUÍ</a></p>
                    )}
                  </div>
                  {p.status === "validado" ? <span className="inline-flex items-center gap-1 text-xs font-bold text-[#22A860]"><CheckCircle2 className="w-4 h-4" /> Validado</span>
                    : p.status === "rechazado" ? <span className="inline-flex items-center gap-1 text-xs font-bold text-[#C0392B]"><XCircle className="w-4 h-4" /> Rechazado</span>
                    : <span className="inline-flex items-center gap-1 text-xs font-bold text-[#E9A400]"><Clock className="w-4 h-4" /> En revisión</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ayuda Financiera Familiar */}
        <div>
          <h2 className="font-extrabold text-[#0A1C30] mb-3 flex items-center gap-2"><HeartHandshake className="w-5 h-5 text-[#E9A400]" /> Ayuda Financiera Familiar</h2>
          {aids.length === 0 ? (
            <p className="text-sm text-[#6B7688]">Por ahora no hay apoyos disponibles. Te avisaremos cuando Supera abra becas u ofertas.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="fin-aids">
              {aids.map((a) => (
                <div key={a.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
                  <div className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-[#E9A400]" />
                    <p className="font-extrabold text-[#0A1C30]">{a.titulo}</p>
                  </div>
                  <p className="text-sm text-[#6B7688] mt-1">{a.descripcion}</p>
                  {a.valor && <p className="text-sm font-bold text-[#22A860] mt-1">{a.valor}</p>}
                  <button data-testid={`fin-aid-pay-${a.id}`} onClick={() => openPay(a)} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-navy text-white font-bold px-4 py-2 text-sm hover:bg-[#0d2440]">
                    Elegir y pagar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {payOpen && <PayModal info={info} st={st} aid={aidCtx} concepto={conceptoCtx} hijoId={hijoId} onClose={() => setPayOpen(false)} onDone={() => { setPayOpen(false); setThanks(true); load(); }} />}

      <Dialog open={thanks} onOpenChange={setThanks}>
        <DialogContent data-testid="fin-thanks-dialog">
          <DialogHeader><DialogTitle>Pago en proceso de revisión</DialogTitle></DialogHeader>
          <p className="text-[#2A3446]">Agradecemos el esfuerzo que haces al realizar tu pago para que <b>Supera Leadership</b> sea una realidad. Revisaremos tu comprobante y te avisaremos pronto.</p>
          <DialogFooter><button data-testid="fin-thanks-ok" onClick={() => setThanks(false)} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5">OK</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PayModal({ info, st, aid, concepto, hijoId, onClose, onDone }) {
  const isConcept = !!concepto;
  // Datos de pago: si el concepto trae liga/cuenta propia, se usan; si no, los globales.
  const hasConceptTransfer = isConcept && (concepto.banco || concepto.clabe || concepto.cuenta || concepto.titular || concepto.instrucciones);
  const payLink = (isConcept && concepto.paypal_link) ? concepto.paypal_link : info.paypal_link;
  const mexicoData = hasConceptTransfer
    ? { banco: concepto.banco, titular: concepto.titular, clabe: concepto.clabe, cuenta: concepto.cuenta, instrucciones: concepto.instrucciones }
    : info.mexico;
  const methods = info.is_mexico
    ? [...(info.mexico?.paypal_enabled ? [["paypal", "PayPal", CreditCard]] : []), ...((info.mexico?.transfer_enabled || hasConceptTransfer) ? [["transferencia", "Transferencia", Landmark]] : [])]
    : [["paypal", "PayPal", CreditCard]];
  const [metodo, setMetodo] = useState(methods[0]?.[0] || "paypal");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    programa: st.programa || info.programas[0]?.programa || "",
    monto: isConcept ? (concepto.monto || 0) : (st.precio_mensual || 0),
    moneda: isConcept ? (concepto.moneda || (info.is_mexico ? "MXN" : "USD")) : (st.moneda || (info.is_mexico ? "MXN" : "USD")),
    mes_cubierto: isConcept ? "" : (st.proximo_mes_pendiente || (st.meses?.[0]?.mes || "")),
    fecha_pago: new Date().toISOString().slice(0, 10),
    referencia_supera: "", nombre_candidato: "", pais: st.pais || "",
    whatsapp: "", nombre_pagador: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const base = Number(f.monto) || 0;
  const comEnabled = metodo === "paypal" ? info.paypal_comision_enabled : info.transfer_comision_enabled;
  const comPct = Number(metodo === "paypal" ? info.paypal_comision_pct : info.transfer_comision_pct) || 0;
  const comNota = metodo === "paypal" ? info.paypal_comision_nota : info.transfer_comision_nota;
  const comision = comEnabled && comPct > 0 ? Math.round(base * comPct) / 100 : 0;
  const total = Math.round((base + comision) * 100) / 100;

  const submit = async () => {
    if (!String(f.programa).trim()) { toast.error("Selecciona el programa que pagas."); return; }
    if (!(Number(f.monto) > 0)) { toast.error("Indica el monto del pago."); return; }
    const reqs = [["fecha_pago", "fecha de pago"], ["referencia_supera", "referencia Supera"], ["nombre_candidato", "nombre del candidato"], ["pais", "país"], ["whatsapp", "WhatsApp"], ["nombre_pagador", "nombre de quien paga"]];
    if (!isConcept) reqs.unshift(["mes_cubierto", "mes que pagas"]);
    for (const [k, lbl] of reqs) {
      if (!String(f[k]).trim()) { toast.error(`Completa: ${lbl}.`); return; }
    }
    if (!file) { toast.error("Sube la imagen del comprobante."); return; }
    setSaving(true);
    try {
      const up = await financeApi.upload(file);
      await financeApi.submitPayment({ ...f, monto: total, metodo, comprobante_path: up.path, comprobante_name: up.name, aid_id: aid?.id || null, concepto: concepto?.titulo || "", concepto_id: concepto?.id || null, hijo_id: hijoId || null });
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fin-pay-modal">
        <DialogHeader><DialogTitle>Realizar pago{concepto ? ` · ${concepto.titulo}` : aid ? ` · ${aid.titulo}` : ""}</DialogTitle></DialogHeader>

        {/* Payment method + instructions */}
        <div className="flex gap-2">
          {methods.map(([v, l, Ic]) => (
            <button key={v} data-testid={`fin-method-${v}`} onClick={() => setMetodo(v)} className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 font-bold text-xs ${metodo === v ? "bg-navy text-white" : "bg-[#F4F6F9] text-[#0A1C30]"}`}>
              <Ic className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
        {metodo === "paypal" && payLink && (
          <a href={payLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-xl bg-[#003087] text-white font-bold px-4 py-2.5 text-sm">
            Pagar en PayPal <ExternalLink className="w-4 h-4" />
          </a>
        )}
        {metodo === "transferencia" && mexicoData && (
          <div className="rounded-xl bg-[#F4F6F9] p-3 text-sm text-[#2A3446]">
            <p className="font-bold">Datos para transferencia</p>
            <p>Banco: {mexicoData.banco || "—"} · Titular: {mexicoData.titular || "—"}</p>
            <p>CLABE: {mexicoData.clabe || "—"} · Cuenta: {mexicoData.cuenta || "—"}</p>
            {mexicoData.instrucciones && <p className="mt-1 text-xs">{mexicoData.instrucciones}</p>}
          </div>
        )}

        <div className="rounded-xl bg-[#FFF7E6] ring-1 ring-[#E9A400]/40 p-3 mt-1" data-testid="fin-aviso-comprobante">
          <p className="text-xs font-extrabold text-[#8A6D1B] leading-relaxed">
            ⚠️ UNA VEZ REALIZADO SU PAGO, NO OLVIDE ENVIAR EL COMPROBANTE DIRECTAMENTE DE ESTA PLATAFORMA, PARA QUE SU ESTADO DE CUENTA SE ACTUALICE.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
          <div><Label className="mb-1 block font-bold text-xs">Programa que pagas</Label>
            <Select value={f.programa} onValueChange={(v) => set("programa", v)}>
              <SelectTrigger data-testid="fin-programa"><SelectValue /></SelectTrigger>
              <SelectContent>{info.programas.map((p) => <SelectItem key={p.programa} value={p.programa}>{p.programa}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!isConcept && (
            <div><Label className="mb-1 block font-bold text-xs">Mes que pagas</Label>
              <Select value={f.mes_cubierto} onValueChange={(v) => set("mes_cubierto", v)}>
                <SelectTrigger data-testid="fin-mes"><SelectValue placeholder="Mes" /></SelectTrigger>
                <SelectContent>{(st.meses || []).map((m) => <SelectItem key={m.mes} value={m.mes} disabled={m.pagado}>{fmtMes(m.mes)}{m.pagado ? " (pagado)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="mb-1 block font-bold text-xs">Monto ({f.moneda})</Label><Input data-testid="fin-monto" type="number" value={f.monto} onChange={(e) => set("monto", e.target.value)} disabled={isConcept} className={isConcept ? "bg-[#F4F6F9]" : ""} /></div>
          <div><Label className="mb-1 block font-bold text-xs">Fecha de pago</Label><Input data-testid="fin-fecha" type="date" value={f.fecha_pago} onChange={(e) => set("fecha_pago", e.target.value)} /></div>
          <div><Label className="mb-1 block font-bold text-xs">Referencia Supera</Label><Input data-testid="fin-ref" value={f.referencia_supera} onChange={(e) => set("referencia_supera", e.target.value)} /></div>
          <div><Label className="mb-1 block font-bold text-xs">Nombre del candidato/a</Label><Input data-testid="fin-cand" value={f.nombre_candidato} onChange={(e) => set("nombre_candidato", e.target.value)} /></div>
          <div><Label className="mb-1 block font-bold text-xs">País</Label><Input data-testid="fin-pais" value={f.pais} onChange={(e) => set("pais", e.target.value)} /></div>
          <div><Label className="mb-1 block font-bold text-xs">WhatsApp de contacto</Label><Input data-testid="fin-wa" value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></div>
          <div className="col-span-2"><Label className="mb-1 block font-bold text-xs">Nombre de quien realiza el pago</Label><Input data-testid="fin-pagador" value={f.nombre_pagador} onChange={(e) => set("nombre_pagador", e.target.value)} /></div>
          <div className="col-span-2">
            <Label className="mb-1 block font-bold text-xs">Comprobante (imagen o PDF)</Label>
            <label className="flex items-center gap-2 rounded-xl border border-dashed border-black/20 px-4 py-3 cursor-pointer hover:bg-[#F4F6F9]">
              <Upload className="w-4 h-4 text-[#E9A400]" />
              <span className="text-sm text-[#6B7688]">{file ? file.name : "Toca para subir tu comprobante"}</span>
              <input data-testid="fin-file" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            </label>
          </div>
        </div>

        {comEnabled && comPct > 0 && (
          <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-1" data-testid="fin-comision-box">
            <div className="flex items-center justify-between text-sm text-[#2A3446]"><span>Subtotal</span><span className="font-bold">{base.toLocaleString()} {f.moneda}</span></div>
            <div className="flex items-center justify-between text-sm text-[#2A3446]"><span>Comisión por plataforma ({comPct}%)</span><span className="font-bold">+{comision.toLocaleString()} {f.moneda}</span></div>
            {comNota && <p className="text-xs text-[#6B7688] pt-1">{comNota}</p>}
            <div className="flex items-center justify-between text-base font-extrabold text-[#0A1C30] border-t border-black/10 pt-2 mt-1"><span>Total a pagar</span><span data-testid="fin-total">{total.toLocaleString()} {f.moneda}</span></div>
          </div>
        )}

        <DialogFooter>
          <button data-testid="fin-submit-btn" onClick={submit} disabled={saving} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5 disabled:opacity-60">
            {saving ? "Enviando..." : "Enviar comprobante"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
